import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/useEntity';
import { fmt } from '../lib/formatters';
import { Badge } from './ui/Badge';
import type { NavItem } from './Layout';

/**
 * Paleta de comandos (Ctrl+K / botón «Buscar» del menú), rediseño UX fase 2.
 * Busca en productos, pedidos, cotizaciones y proveedores del cliente activo
 * (GET /api/buscar) y en las entradas del menú. Prefijos `p:` `o:` `c:` `v:`
 * acotan el tipo. Teclado: ↑↓ moverse, ↵ abrir, Esc cerrar.
 */

type Resultado = {
  productos: { id: string; sku: string; ean: string | null; part_number: string | null; nombre: string; estado: string; estado_nombre: string | null; estado_color: string | null; cliente_codigo: string }[];
  pedidos: { id: string; numero_cliente: string | null; numero_approx: string | null; fecha_pedido: string; estado: string; cliente_codigo: string; lineas: number; skus: string | null }[];
  cotizaciones: { id: string; fecha: string; unidades: number | null; pvpr: number | null; sku: string; producto_nombre: string; cliente_codigo: string }[];
  proveedores: { id: string; nombre: string; pais: string | null; activo: boolean }[];
};
type Item = { key: string; grupo: string; titulo: string; sub?: string; extra?: React.ReactNode; to: string };

export function CommandPalette({ open, onClose, clienteCodigo, menu }: { open: boolean; onClose: () => void; clienteCodigo: string; menu: NavItem[] }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const h = window.setTimeout(() => setDebounced(q.trim()), 150); return () => window.clearTimeout(h); }, [q]);
  useEffect(() => { if (open) { setQ(''); setDebounced(''); setIdx(0); requestAnimationFrame(() => input.current?.focus()); } }, [open]);

  const busqueda = useQuery({
    queryKey: ['buscar', clienteCodigo, debounced],
    queryFn: () => api.get<Resultado>(`/buscar?q=${encodeURIComponent(debounced)}&cliente=${encodeURIComponent(clienteCodigo)}`),
    enabled: open && debounced.replace(/^[pocv]:\s*/i, '').length >= 2,
    staleTime: 30_000,
  });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    const texto = debounced.replace(/^[pocv]:\s*/i, '').toLowerCase();
    const r = busqueda.data;
    if (r) {
      for (const p of r.productos) out.push({ key: `p-${p.id}`, grupo: `Productos · ${p.cliente_codigo}`, titulo: p.nombre, sub: [p.sku, p.ean ? `EAN ${p.ean}` : null, p.part_number].filter(Boolean).join(' · '), extra: <Badge color={p.estado_color} dot>{p.estado_nombre ?? p.estado}</Badge>, to: `/${p.cliente_codigo.toLowerCase()}/productos/${p.id}` });
      for (const o of r.pedidos) out.push({ key: `o-${o.id}`, grupo: `Pedidos · ${o.cliente_codigo}`, titulo: `Pedido ${o.numero_cliente ?? o.numero_approx ?? o.id.slice(0, 8)}`, sub: `${fmt.date(o.fecha_pedido)} · ${o.lineas} línea${o.lineas === 1 ? '' : 's'}${o.skus ? ` · ${o.skus}` : ''}`, extra: <Badge tone="neutral">{o.estado.toLowerCase().replace(/_/g, ' ')}</Badge>, to: `/${o.cliente_codigo.toLowerCase()}/pedidos/${o.id}` });
      for (const c of r.cotizaciones) out.push({ key: `c-${c.id}`, grupo: `Cotizaciones · ${c.cliente_codigo}`, titulo: `${c.sku} · ${c.producto_nombre}`, sub: `${fmt.date(c.fecha)}${c.unidades ? ` · ${fmt.int(c.unidades)} uds` : ''}${c.pvpr != null ? ` · PVPr ${fmt.eur(c.pvpr)}` : ''}`, to: `/${c.cliente_codigo.toLowerCase()}/cotizaciones/${c.id}` });
      for (const v of r.proveedores) out.push({ key: `v-${v.id}`, grupo: 'Proveedores', titulo: v.nombre, sub: v.pais ?? undefined, extra: v.activo ? undefined : <Badge tone="neutral">inactivo</Badge>, to: `/proveedores/listado/${v.id}` });
    }
    // Entradas de menú (siempre, filtradas por el texto; sin prefijo de tipo).
    if (!/^[pocv]:/i.test(debounced)) {
      for (const m of menu) {
        if (!texto || m.label.toLowerCase().includes(texto)) out.push({ key: `m-${m.to}`, grupo: 'Ir a', titulo: m.label, sub: m.to, to: m.to });
      }
    }
    return out;
  }, [busqueda.data, debounced, menu]);

  useEffect(() => { setIdx(0); }, [items.length, debounced]);

  const abrir = (it: Item) => { onClose(); navigate(it.to); };

  if (!open) return null;
  const grupos: { grupo: string; items: Item[] }[] = [];
  for (const it of items) { const g = grupos.find((x) => x.grupo === it.grupo); if (g) g.items.push(it); else grupos.push({ grupo: it.grupo, items: [it] }); }
  let n = -1;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-900/40 p-4 pt-16 sm:pt-24" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-label="Buscar" className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <span aria-hidden="true" className="text-slate-400">⌕</span>
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(items.length - 1, i + 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
              else if (e.key === 'Enter') { e.preventDefault(); const it = items[idx]; if (it) abrir(it); }
              else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            }}
            placeholder={`Buscar en ${clienteCodigo}: producto, pedido, cotización, proveedor o pantalla…`}
            aria-label="Buscar"
            className="flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
          <kbd className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">Esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {busqueda.isFetching && <div className="px-4 py-2 text-xs text-slate-400">Buscando…</div>}
          {!busqueda.isFetching && debounced.length >= 2 && items.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-500">Nada que coincida con «{debounced}».</div>}
          {grupos.map((g) => (
            <div key={g.grupo}>
              <div className="px-4 pb-0.5 pt-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">{g.grupo}</div>
              {g.items.map((it) => {
                n += 1; const activo = n === idx; const i = n;
                return (
                  <button key={it.key} type="button" onMouseEnter={() => setIdx(i)} onClick={() => abrir(it)} className={`flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left text-sm ${activo ? 'bg-accent-50 text-slate-900' : 'text-slate-700'}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{it.titulo}</span>
                      {it.sub && <span className="block truncate font-mono text-[11px] text-slate-500">{it.sub}</span>}
                    </span>
                    {it.extra}
                    {activo && <kbd className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">↵</kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
          <span><b>p:</b> productos</span><span><b>o:</b> pedidos</span><span><b>c:</b> cotizaciones</span><span><b>v:</b> proveedores</span><span className="ml-auto">↑↓ moverse · ↵ abrir · ? atajos</span>
        </div>
      </div>
    </div>
  );
}
